import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IAbstractModel } from "model/abstract.model";
import {
  FlowConsignadoDTO,
  FlowConsignadoItemDTO,
  flowConsignadoItemOf,
  FlowConsignadoQtdValoresDTO,
} from "pages/consignado/detail/consignado-detail.model";
import { SituacaoConsignado } from "pages/consignado/situacao-consignado.model";
import {
  IProduto,
  TipoDesconto,
} from "pages/registra-venda/registra-venda.model";
import {
  getDescItem,
  getPorcentagemDesconto,
  RetornoDesconto,
} from "utils/desconto-utils";
import { deepClone, deepCopy, deepReset } from "utils/util";

type StateTeste = {
  uuid: string | null;
  version: number | null;
  cliente: IAbstractModel | null;
  vendedor: IAbstractModel | null;
  numero: number | null;
  situacao: SituacaoConsignado;
  qtdValores: FlowConsignadoQtdValoresDTO;
  tableData: { [situacao: string]: FlowConsignadoItemDTO[] };
  tabelaPreco: IAbstractModel | null;
  deposito: IAbstractModel | null;
  shouldSave: boolean;
  descontoFixo: null | number;
};

const initialState: StateTeste = {
  uuid: null,
  version: null,
  cliente: null,
  vendedor: null,
  numero: null,
  situacao: SituacaoConsignado.ABERTO,
  qtdValores: {
    quantidadeConsignada: 0,
    valorConsignado: 0,
    quantidadeDevolvida: 0,
    valorDevolvido: 0,
    quantidadeVendida: 0,
    valorVendido: 0,
    saldo: 0,
    valorSaldo: 0,
  },
  tabelaPreco: null,
  deposito: null,
  tableData: {
    [SituacaoConsignado.ABERTO.toString()]: [],
    [SituacaoConsignado.SIMBOLICO.toString()]: [],
    [SituacaoConsignado.DIVERGENTE.toString()]: [],
  },
  shouldSave: false,
  descontoFixo: null,
};

const consignadoSlice = createSlice({
  name: "consignadoSlice",
  initialState,
  reducers: {
    setIdsInfo: (
      s,
      {
        payload,
      }: PayloadAction<{
        uuid?: string | null;
        version?: number | null;
        numero?: number;
      }>
    ) => {
      s.uuid = payload.uuid ?? null;
      s.version = payload.version ?? 0;
      s.numero = payload.numero || null;
    },
    setConsignado: (s, { payload }: PayloadAction<FlowConsignadoDTO>) => {
      s.uuid = payload.uuid ?? null;
      s.version = payload.version ?? 0;
      s.numero = payload.numero ?? null;
      if (s.cliente?.uuid !== payload.cliente?.uuid) {
        s.cliente = payload.cliente;
      }
      s.vendedor = payload.vendedor;
      s.tableData = {
        [SituacaoConsignado.ABERTO.toString()]: payload.itensAbertos,
        [SituacaoConsignado.SIMBOLICO.toString()]: payload.itensSimbolicos,
        [SituacaoConsignado.DIVERGENTE.toString()]: payload.itensPendentes,
      };
      s.situacao = payload.situacao ?? SituacaoConsignado.ABERTO;
      s.qtdValores = payload.qtdValores ?? s.qtdValores;
      s.tabelaPreco = payload.tabelaPreco;
      s.deposito = payload.deposito;
    },

    setTabelaPrecoConsignado: (
      s,
      { payload }: PayloadAction<IAbstractModel | null>
    ) => {
      s.tabelaPreco = payload;
      s.shouldSave = true;
    },
    setDepositoConsignado: (
      s,
      { payload }: PayloadAction<IAbstractModel | null>
    ) => {
      s.deposito = payload;
      s.shouldSave = true;
    },
    resetConsignado: (s) => {
      deepReset(s, initialState);
    },

    atualizaValoresItensConsignado: (
      s,
      { payload }: PayloadAction<IProduto[]>
    ) => {
      const itens = deepClone(s.tableData[SituacaoConsignado.ABERTO]);
      for (let i = itens.length - 1; i >= 0; i--) {
        const novoItem = payload.find((el) => itens[i].sku.uuid === el?.uuid);
        if (!novoItem) {
          itens.splice(i, 1);
        } else {
          const novoPreco = novoItem.preco;
          const item = itens[i];
          const { valorDesconto, valorTotalBruto, valorTotalLiquido } =
            getDescItem(
              item.quantidade,
              novoPreco,
              item.percentualDesconto,
              TipoDesconto.PERCENTUAL
            );
          item.valorUnitario = novoPreco;
          item.sku.preco = novoPreco;
          item.valorTotalLiquido = valorTotalLiquido;
          item.valorDesconto = valorDesconto;
          item.valorTotalBruto = valorTotalBruto;
        }
      }

      s.tableData[SituacaoConsignado.ABERTO] = itens;
      s.shouldSave = true;
    },

    addItemConsignado: (s, { payload }: PayloadAction<IProduto>) => {
      const tableDataCopy = deepCopy(s.tableData);
      const item = flowConsignadoItemOf(payload);
      applyUpdate(item, { type: "changeDesconto", valor: s.descontoFixo ?? 0 });
      tableDataCopy[SituacaoConsignado.ABERTO.toString()].unshift(item);
      s.tableData = tableDataCopy;
      s.shouldSave = true;
    },

    removeItemConsignado: (s, { payload }: PayloadAction<IProduto>) => {
      const tableDataCopy = deepCopy(s.tableData);
      const list = tableDataCopy[SituacaoConsignado.ABERTO.toString()];
      const index = list.findIndex((j) => j.sku.uuid === payload.uuid);
      if (index !== -1) {
        list.splice(index, 1);
      }
      s.tableData = tableDataCopy;
      s.shouldSave = true;
    },

    updateItemTableDataConsignado: (
      s,
      {
        payload,
      }: PayloadAction<{
        situacao: SituacaoConsignado | string;
        update: UpdateItemConsignadoOptions;
        index?: number;
      }>
    ) => {
      const copyTableData = deepClone(s.tableData);
      const porSituacao = copyTableData[payload.situacao];

      if (payload.index !== undefined) {
        const item = porSituacao[payload.index];
        applyUpdate(item, payload.update);
      } else {
        porSituacao.forEach((item) => {
          applyUpdate(item, payload.update);
        });
      }

      s.tableData = copyTableData;
      s.shouldSave = true;
    },

    deleteItemConsignado: (
      prev,
      {
        payload,
      }: PayloadAction<{
        situacao: SituacaoConsignado;
        index: number;
      }>
    ) => {
      const { situacao, index } = payload;
      const copyTableData = deepCopy(prev.tableData);

      copyTableData[situacao] = copyTableData[situacao].filter(
        (_, i) => i !== index
      );

      prev.tableData = copyTableData;
      prev.shouldSave = true;
    },

    setClienteConsignado: (
      s,
      {
        payload,
      }: PayloadAction<{ cliente: IAbstractModel | null; shouldSave: boolean }>
    ) => {
      s.cliente = payload.cliente;
      s.shouldSave = payload.shouldSave;
    },
    setVendedorConsignado: (
      s,
      {
        payload,
      }: PayloadAction<{ vendedor: IAbstractModel | null; shouldSave: boolean }>
    ) => {
      s.vendedor = payload.vendedor;
      s.shouldSave = payload.shouldSave;
    },

    concluirSalvarConsignado: (s) => {
      s.shouldSave = false;
    },

    setDescontoFixoConsignado: (
      s,
      { payload }: PayloadAction<number | null>
    ) => {
      s.descontoFixo = payload;
    },
  },
});

export const {
  setConsignado,
  setIdsInfo,
  resetConsignado,
  setTabelaPrecoConsignado,
  setDepositoConsignado,
  atualizaValoresItensConsignado,
  updateItemTableDataConsignado,
  deleteItemConsignado,
  addItemConsignado,
  removeItemConsignado,
  setClienteConsignado,
  setVendedorConsignado,
  concluirSalvarConsignado,
  setDescontoFixoConsignado,
} = consignadoSlice.actions;
export const consignadoReducer = consignadoSlice.reducer;

type UpdateItemConsignadoOptions =
  | { type: "toggleSelection"; checked: boolean }
  | { type: "toggleTipoDesconto" }
  | { type: "changeQuantidade"; quantidade: number }
  | { type: "changeDesconto"; valor: number };

const applyUpdate = (
  item: FlowConsignadoItemDTO,
  opt: UpdateItemConsignadoOptions
) => {
  const updateValores = (
    item: FlowConsignadoItemDTO,
    valores: RetornoDesconto
  ) => {
    Object.assign(item, valores);
  };

  switch (opt.type) {
    case "toggleSelection":
      item.selecionado = opt.checked;
      break;

    case "changeQuantidade":
      const { valorUnitario, percentualDesconto } = item;
      const { quantidade } = opt;
      const valores = getPorcentagemDesconto(
        quantidade,
        valorUnitario,
        percentualDesconto
      );
      item.quantidade = quantidade;
      updateValores(item, valores);
      break;

    case "toggleTipoDesconto":
      item.tipoDesconto =
        item.tipoDesconto === TipoDesconto.PERCENTUAL
          ? TipoDesconto.VALOR
          : TipoDesconto.PERCENTUAL;
      break;

    case "changeDesconto":
      const { valor } = opt;

      const valoresDescontos = getDescItem(
        item.quantidade,
        item.valorUnitario,
        valor,
        item.tipoDesconto
      );
      updateValores(item, valoresDescontos);
      break;
  }
};
